import { Box, Paper, Typography, Collapse, Button, Grid, TextField } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useState } from "react";
import SalespersonVsMopSummary from "./SalePersonVsMopSummary/SalePersonVsMopSummary.js";
import StoreMopSummaryTable from "./StoreMopSummary/StoreMopSummary.js";
import LedgerVsMopSummary from "./BankStatementSummary/BankStatementSummary.js";

/* -----------------------------
   Utility: Yesterday Date
----------------------------- */
const getYesterday = () => {
	const d = new Date();
	d.setDate(d.getDate() - 1);
	return d.toISOString().split("T")[0];
};

/* -----------------------------
   Utility: Format Date for Display
----------------------------- */
const formatDisplayDate = (dateStr) => {
	if (!dateStr) return "";
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-IN", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
};

const Summary = () => {
	const [startDate, setStartDate] = useState(getYesterday());
	const [endDate, setEndDate] = useState(getYesterday());
	const [runKey, setRunKey] = useState(0);
	const [showFilters, setShowFilters] = useState(false);

	/* -----------------------------
	   FIRST LOAD (YESTERDAY)
	----------------------------- */
	useEffect(() => {
		// trigger initial load once
		setRunKey(1);
	}, []);

	/* -----------------------------
	   Handlers
	----------------------------- */
	const handleToggleFilters = () => {
		setShowFilters((prev) => !prev);
	};

	const handleRun = () => {
		setRunKey((prev) => prev + 1);
		setShowFilters(false);
	};

	return (
		<Box p={3} sx={{ width: "100%", height: "calc(100vh - 100px)", overflowY: "auto", bgcolor: "#f9fafb" }}>
			{/* ================= HEADER BAR ================= */}
			<Paper
				elevation={0}
				sx={{
					p: 3,
					mb: 3,
					borderRadius: 3,
					border: "1px solid #e0e0e0",
					bgcolor: "#fff",
				}}>
				<Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
					{/* -------- LEFT: SUMMARY CONTEXT -------- */}
					<Box>
						<Typography variant="h5" fontWeight={700} color="text.primary" letterSpacing={-0.5}>
							Dashboard Summary
						</Typography>
						<Typography variant="body2" color="text.secondary" mt={0.5}>
							Overview for{" "}
							<Box component="span" fontWeight={600} color="primary.main">
								{formatDisplayDate(startDate)}
							</Box>{" "}
							to{" "}
							<Box component="span" fontWeight={600} color="primary.main">
								{formatDisplayDate(endDate)}
							</Box>
						</Typography>
					</Box>

					{/* -------- RIGHT: FILTER ICON -------- */}
					<Button
						variant={showFilters ? "contained" : "outlined"}
						color="primary"
						startIcon={<FilterListIcon />}
						onClick={handleToggleFilters}
						sx={{
							borderRadius: 2,
							textTransform: "none",
							fontWeight: 600,
							px: 3,
						}}>
						Filters
					</Button>
				</Box>

				{/* ================= FILTER PANEL ================= */}
				<Collapse in={showFilters}>
					<Box mt={3} pt={3} borderTop="1px solid #f0f0f0">
						<Grid container spacing={2} alignItems="flex-end">
							<Grid item xs={12} sm={6} md={3}>
								<TextField
									fullWidth
									label="Start Date"
									type="date"
									value={startDate}
									onChange={(e) => setStartDate(e.target.value)}
									InputLabelProps={{ shrink: true }}
									size="small"
									inputProps={{ max: endDate }}
									sx={{
										"& .MuiOutlinedInput-root": {
											borderRadius: 2,
										},
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<TextField
									fullWidth
									label="End Date"
									type="date"
									value={endDate}
									onChange={(e) => setEndDate(e.target.value)}
									InputLabelProps={{ shrink: true }}
									size="small"
									inputProps={{ min: startDate }}
									sx={{
										"& .MuiOutlinedInput-root": {
											borderRadius: 2,
										},
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={12} md={2}>
								<Button
									fullWidth
									variant="contained"
									color="primary"
									onClick={handleRun}
									startIcon={<SearchIcon />}
									sx={{
										borderRadius: 2,
										textTransform: "none",
										fontWeight: 600,
										height: 40,
										boxShadow: "none",
										"&:hover": {
											boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
										},
									}}>
									Apply
								</Button>
							</Grid>
						</Grid>
					</Box>
				</Collapse>
			</Paper>

			{/* ================= CHILD COMPONENT ================= */}
			<Grid container spacing={1.5}>
				{/* BANK STATEMENT */}
				<Grid item xs={12} md={6}>
					<LedgerVsMopSummary startDate={startDate} endDate={endDate} runKey={runKey} />
				</Grid>

				{/* SALE PERSON VS MOP */}
				<Grid item xs={12} md={6}>
					<SalespersonVsMopSummary startDate={startDate} endDate={endDate} runKey={runKey} />
				</Grid>

				{/* STORE MOP SUMMARY */}
				<Grid item xs={12}>
					<StoreMopSummaryTable startDate={startDate} endDate={endDate} runKey={runKey} />
				</Grid>
			</Grid>
		</Box>
	);
};

export default Summary;
